import { useMemo, type FunctionComponent } from "react";
import ReactMarkdown, { type Components } from "react-markdown";

import { normalizeChallengeMarkdown } from "@fitness-app/data-models/entities/ChallengeFeed";

// Posts are authored in Markdown (same content the mobile app renders), so the admin preview has to
// use the same source of truth — a plain <Typography.Paragraph> would show raw `**bold**` markers.
const ChallengeFeedMarkdown: FunctionComponent<{ content: string }> = ({ content }) => {
  const components: Components = useMemo(
    () => ({
      p: ({ children }) => <p className="mb-2 whitespace-pre-wrap last:mb-0">{children}</p>,
      ul: ({ children }) => <ul className="mb-2 list-disc pl-5 last:mb-0">{children}</ul>,
      ol: ({ children }) => <ol className="mb-2 list-decimal pl-5 last:mb-0">{children}</ol>,
      li: ({ children }) => <li className="mb-0.5">{children}</li>,
      a: ({ children, ...props }) => (
        <a className="text-blue-600 underline" target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      ),
      code: ({ children, ...props }) => (
        <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em]" {...props}>
          {children}
        </code>
      ),
      pre: ({ children }) => (
        <pre className="mb-2 overflow-x-auto rounded bg-gray-100 p-2 text-sm last:mb-0">{children}</pre>
      ),
      blockquote: ({ children }) => (
        <blockquote className="mb-2 border-l-4 border-gray-300 pl-3 text-gray-600 last:mb-0">{children}</blockquote>
      ),
      // A post is a short announcement, not a document — headings read better as emphasized lines.
      h1: ({ children }) => <p className="mb-2 font-semibold last:mb-0">{children}</p>,
      h2: ({ children }) => <p className="mb-2 font-semibold last:mb-0">{children}</p>,
      h3: ({ children }) => <p className="mb-2 font-semibold last:mb-0">{children}</p>,
    }),
    [],
  );

  const markdown = normalizeChallengeMarkdown(content);

  if (!markdown) {
    return null;
  }

  return <ReactMarkdown components={components}>{markdown}</ReactMarkdown>;
};

export default ChallengeFeedMarkdown;
